import React, { useState } from 'react';
import Icon from './Icon';

function LandingPage({ onGetStarted, onSignIn }) {
    // eslint-disable-next-line no-unused-vars
    const [email, setEmail] = useState('');

    // `money: true` is the only slot that wears the brand green here — the same
    // semantic the logged-in home uses for "Total opportunity found". Counts stay
    // in neutral ink so green keeps meaning "dollars Audee found".
    const STATS = [
        { value: '448+', label: 'Audit rules' },
        { value: '$40K+', label: 'Avg opportunity found', money: true },
        { value: '3', label: 'Free audits to start' },
    ];

    const FEATURES = [
        {
            icon: 'coins',
            title: 'Find unbilled opportunities',
            desc: 'Catch legitimate line items your estimator missed — companion items, code upgrades, 2025/2026 new Xactimate items, and more.',
        },
        {
            icon: 'flag',
            title: 'Flag billing errors before submission',
            desc: 'Identify duplicate line items, overbilled equipment, excessive labor hours, and unsupported charges before they become disputes.',
        },
        {
            icon: 'clipboard',
            title: 'Prepare for adjuster review',
            desc: 'Know exactly which line items will be challenged. Walk into every negotiation with documentation ready and positions prepared.',
        },
        {
            icon: 'grid',
            title: 'Xactimate & T&M coverage',
            desc: '329 Xactimate rules covering water, fire, mold, roofing, and construction. 110 T&M rules covering Schedule A through E compliance.',
        },
        {
            icon: 'zap',
            title: 'Instant results',
            desc: 'Upload your PDF and get a full audit report in seconds — not hours. Every finding includes a recommended fix and dollar impact.',
        },
        {
            icon: 'target',
            title: 'Built for your industry',
            desc: 'Designed specifically for restoration contractors, public adjusters, and insurance adjusters. Not a generic document tool.',
        },
    ];

    const PLANS = [
        {
            name: 'T&M',
            price: 299,
            desc: 'For T&M mitigation billing',
            features: ['110+ T&M audit rules', 'Schedule A/B/C/D/E compliance', 'Unlimited T&M audits', 'PDF export'],
            highlighted: false,
        },
        {
            name: 'Xactimate',
            price: 399,
            desc: 'For Xactimate estimate auditing',
            features: ['329+ Xactimate rules', 'Water, fire, mold, roofing & construction', 'Unlimited Xactimate audits', 'PDF export'],
            highlighted: false,
        },
        {
            name: 'All Access',
            price: 499,
            desc: 'Full platform — both document types',
            features: ['448+ total audit rules', 'Xactimate + T&M coverage', 'Unlimited audits on all types', 'PDF export'],
            highlighted: true,
        },
    ];

    return (
        <div className="landing-page">

            {/* Nav */}
            <nav className="landing-nav">
                <div className="landing-nav-logo">Audee<span className="logo-dot">.</span></div>
                <div className="landing-nav-right">
                    <button className="landing-nav-link" onClick={onSignIn || onGetStarted}>Sign In</button>
                    <button className="landing-nav-cta" onClick={onGetStarted}>Start free trial</button>
                </div>
            </nav>

            {/* Hero */}
            <section className="landing-hero">
                <div className="landing-hero-inner">
                    <div className="landing-hero-badge">Xactimate & T&M · 448+ audit rules</div>
                    <h1 className="landing-hero-title">
                        Find the money<br />your estimates missed.
                    </h1>
                    <p className="landing-hero-sub">
                        Audee automatically audits Xactimate estimates and T&M invoices — identifying unbilled opportunities, billing errors, and adjuster flags in seconds.
                    </p>
                    <div className="landing-hero-actions">
                        <button className="landing-hero-btn" onClick={onGetStarted}>
                            Start Free — 3 Audits on Us
                        </button>
                        <div className="landing-hero-hint">No credit card required</div>
                    </div>
                    <div className="landing-stats-row">
                        {STATS.map((s, i) => (
                            <div key={i} className="landing-stat">
                                <div className={"landing-stat-value" + (s.money ? " money" : "")}>{s.value}</div>
                                <div className="landing-stat-label">{s.label}</div>
                            </div>
                        ))}
                    </div>
                    <div className="landing-scroll-hint">
                        <div className="landing-scroll-text">Scroll to learn more</div>
                        <div className="landing-scroll-arrow"><Icon name="chevron-down" size={24} /></div>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section className="landing-section">
                <div className="landing-section-inner">
                    <div className="landing-section-label">How it works</div>
                    <h2 className="landing-section-title">Audit any estimate in seconds</h2>
                    <div className="landing-steps">
                        <div className="landing-step">
                            <div className="landing-step-num">01</div>
                            <div className="landing-step-title">Upload your PDF</div>
                            <div className="landing-step-desc">Drop any Xactimate estimate or T&M invoice. Document type is auto-detected.</div>
                        </div>
                        <div className="landing-step-arrow"><Icon name="arrow-right" size={22} /></div>
                        <div className="landing-step">
                            <div className="landing-step-num">02</div>
                            <div className="landing-step-title">448+ rules run instantly</div>
                            <div className="landing-step-desc">Every rule fires against your document — checking for missed items, errors, and flags.</div>
                        </div>
                        <div className="landing-step-arrow"><Icon name="arrow-right" size={22} /></div>
                        <div className="landing-step">
                            <div className="landing-step-num">03</div>
                            <div className="landing-step-title">Get your audit report</div>
                            <div className="landing-step-desc">Every finding includes a rule ID, severity, dollar impact, and recommended fix. Export to PDF.</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="landing-section landing-section-dark">
                <div className="landing-section-inner">
                    <div className="landing-section-label">Features</div>
                    <h2 className="landing-section-title">Everything you need to audit with confidence</h2>
                    <div className="landing-features-grid">
                        {FEATURES.map((f, i) => (
                            <div key={i} className="landing-feature-card">
                                <div className="landing-feature-icon"><Icon name={f.icon} size={24} /></div>
                                <div className="landing-feature-title">{f.title}</div>
                                <div className="landing-feature-desc">{f.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Who it's for */}
            <section className="landing-section">
                <div className="landing-section-inner landing-audience">
                    <div className="landing-audience-left">
                        <div className="landing-section-label">Who it's for</div>
                        <h2 className="landing-section-title">Built for restoration, roofing & construction</h2>
                        <div className="landing-audience-items">
                            <div className="landing-audience-item">
                                <div className="landing-audience-icon"><Icon name="users" size={18} /></div>
                                <div>
                                    <div className="landing-audience-title">Restoration Contractors</div>
                                    <div className="landing-audience-desc">Find unbilled line items before submitting estimates. Recover revenue you're leaving on the table on every T&M and Xactimate job.</div>
                                </div>
                            </div>
                            <div className="landing-audience-item">
                                <div className="landing-audience-icon"><Icon name="users" size={18} /></div>
                                <div>
                                    <div className="landing-audience-title">Roofing Contractors</div>
                                    <div className="landing-audience-desc">Audit every Xactimate roof estimate for missed companions, steep charges, code upgrades, and line items adjusters routinely cut.</div>
                                </div>
                            </div>
                            <div className="landing-audience-item">
                                <div className="landing-audience-icon"><Icon name="users" size={18} /></div>
                                <div>
                                    <div className="landing-audience-title">General Contractors</div>
                                    <div className="landing-audience-desc">Catch missing line items across framing, electrical, plumbing, HVAC, and finish work before your estimate goes to the carrier.</div>
                                </div>
                            </div>
                            <div className="landing-audience-item">
                                <div className="landing-audience-icon"><Icon name="users" size={18} /></div>
                                <div>
                                    <div className="landing-audience-title">Insurance Adjusters</div>
                                    <div className="landing-audience-desc">Flag billing errors and unsupported charges before approval. Defend every decision with documented findings.</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section className="landing-section landing-section-dark">
                <div className="landing-section-inner">
                    <div className="landing-section-label">Pricing</div>
                    <h2 className="landing-section-title">Simple, transparent pricing</h2>
                    <p className="landing-section-sub">Start free with 3 audits. No credit card required.</p>
                    <div className="landing-pricing-grid">
                        {PLANS.map((plan, i) => (
                            <div key={i} className={"landing-pricing-card " + (plan.highlighted ? "highlighted" : "")}>
                                {plan.highlighted && <div className="landing-pricing-badge">Best value</div>}
                                <div className="landing-pricing-name">{plan.name}</div>
                                <div className="landing-pricing-price">
                                    <span className="landing-pricing-dollar">$</span>
                                    <span className="landing-pricing-amount">{plan.price}</span>
                                    <span className="landing-pricing-period">/mo</span>
                                </div>
                                <div className="landing-pricing-desc">{plan.desc}</div>
                                <ul className="landing-pricing-features">
                                    {plan.features.map((f, j) => (
                                        <li key={j}><span className="landing-pricing-check"><Icon name="check" size={15} /></span>{f}</li>
                                    ))}
                                </ul>
                                <button className={"landing-pricing-btn " + (plan.highlighted ? "primary" : "secondary")} onClick={onGetStarted}>
                                    Start free trial
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="landing-cta-section">
                <div className="landing-section-inner" style={{ textAlign: "center" }}>
                    <h2 className="landing-cta-title">Start finding missed revenue today.</h2>
                    <p className="landing-cta-sub">3 free audits. No credit card. Cancel anytime.</p>
                    <button className="landing-hero-btn" onClick={onGetStarted}>
                        Start free trial
                    </button>
                </div>
            </section>

            {/* Footer */}
            <footer className="landing-footer">
                <div className="landing-footer-logo">Audee<span className="logo-dot">.</span></div>
                <div className="landing-footer-tagline">Estimate Auditing. Automated.</div>
                <div className="landing-footer-links">
                    <span>© 2026 Audee</span>
                    <span>·</span>
                    <button className="landing-footer-link" onClick={onSignIn || onGetStarted}>Sign In</button>
                </div>
            </footer>

        </div>
    );
}

export default LandingPage;
